import React, { useMemo } from 'react';
import { View } from 'react-native';
import type { CalendarEvent, CalendarTheme, CalendarStyleConfig } from '../types';
import { styles } from '../styles';
import { DayGrid } from './DayGrid';

interface MonthlyViewProps {
  firstDay: Date;
  daysInMonth: number;
  startingDayOfWeek: number;
  currentDate: Date;
  getEventsForDate: (date: Date) => CalendarEvent[];
  isDateToday: (date: Date) => boolean;
  onDatePress: (day: number) => void;
  onEventDetailsPress?: (event: CalendarEvent) => void;
  onEventPress?: (event: CalendarEvent) => void;
  onShowAllEvents?: (date: Date, events: CalendarEvent[]) => void;
  renderEventBadge: (event: CalendarEvent, small?: boolean) => React.ReactNode;
  theme: CalendarTheme;
  generateCalendarGrid: (
    firstDay: Date,
    daysInMonth: number,
    startingDayOfWeek: number
  ) => (number | null)[];
  getPreviousMonthInfo: (firstDay: Date) => { prevMonthYear: number; prevMonthMonth: number };
  styleConfig?: CalendarStyleConfig;
  isLoading?: boolean;
}

/**
 * MonthlyView Component
 * Displays a full month calendar grid with 5-6 weeks
 */
export const MonthlyView: React.FC<MonthlyViewProps> = ({
  firstDay,
  daysInMonth,
  startingDayOfWeek,
  currentDate,
  getEventsForDate,
  isDateToday,
  onDatePress,
  onEventDetailsPress,
  onEventPress,
  onShowAllEvents,
  renderEventBadge,
  theme,
  generateCalendarGrid,
  getPreviousMonthInfo,
  isLoading = false,
}) => {
  const calendarGridThemeStyle = useMemo(
    () => ({
      backgroundColor: theme.surfaceColor || theme.backgroundColor,
      shadowColor: theme.shadowColor,
      shadowOffset: { width: 0, height: 6 },
      shadowOpacity: 1,
      shadowRadius: 16,
      elevation: 4,
      borderRadius: 8,
      boxShadow: theme.shadowColor ? `0px 6px 16px 0px ${theme.shadowColor}` : undefined,
    }),
    [theme.backgroundColor, theme.shadowColor, theme.surfaceColor]
  );

  const days = generateCalendarGrid(firstDay, daysInMonth, startingDayOfWeek);
  const { prevMonthYear, prevMonthMonth } = getPreviousMonthInfo(firstDay);

  const weeks: (number | null)[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  // Track which indices belong to previous month
  const prevMonthIndices = new Set<number>();
  for (let i = 0; i < startingDayOfWeek; i++) {
    prevMonthIndices.add(i);
  }

  return (
    <View style={[styles.calendarGrid, calendarGridThemeStyle]}>
      {weeks.map((week, weekIndex) => (
        <View key={weekIndex} style={styles.week}>
          {week.map((day, dayIndexInWeek) => {
            const globalDayIndex = weekIndex * 7 + dayIndexInWeek;

            const isPrevMonth = prevMonthIndices.has(globalDayIndex);
            const isCurrentMonth =
              day !== null && day > 0 && day < 1000 && !isPrevMonth;
            const isNextMonth = day !== null && day >= 1000;

            let actualDay = 0;
            let displayMonth = currentDate.getMonth();
            let displayYear = currentDate.getFullYear();

            if (isCurrentMonth) {
              actualDay = day as number;
            } else if (isPrevMonth) {
              actualDay = day as number;
              displayMonth = prevMonthMonth;
              displayYear = prevMonthYear;
            } else if (isNextMonth) {
              actualDay = (day as number) - 1000;
              displayMonth = displayMonth === 11 ? 0 : displayMonth + 1;
              displayYear = displayMonth === 11 ? displayYear + 1 : displayYear;
            }

            const cellDate = new Date(displayYear, displayMonth, actualDay);
            const dayEvents = getEventsForDate(cellDate);
            const today = isDateToday(cellDate);
            const disabled = !isCurrentMonth;

            return (
              <DayGrid
                key={dayIndexInWeek}
                date={cellDate}
                dayEvents={dayEvents}
                today={today}
                disabled={disabled}
                borderColor={theme.borderColor}
                dayNumberColor={theme.dayNumberColor}
                disabledDayColor={theme.disabledDayColor}
                todayAccentColor={theme.todayAccentColor}
                disabledBackgroundColor={theme.disabledBackgroundColor}
                cellBackgroundColor={theme.surfaceColor || theme.backgroundColor}
                overflowBadgeBackgroundColor={theme.overflowBadgeBackgroundColor}
                overflowBadgeTextColor={theme.overflowBadgeTextColor}
                onDatePress={onDatePress}
                onEventDetailsPress={onEventDetailsPress}
                onEventPress={onEventPress}
                onShowAllEvents={() => onShowAllEvents?.(cellDate, dayEvents)}
                renderEventBadge={renderEventBadge}
                isLoading={isLoading}
              />
            );
          })}
        </View>
      ))}
    </View>
  );
};

export default MonthlyView;
